import { useRef, useLayoutEffect } from "react";

const key = (r, c) => `${r}-${c}`;
const isLetter = (k) => k && k.length === 1 && /[a-zA-Z]/.test(k);

export function CrosswordGrid({ layout, size, values, revealed, wrong, active, direction, locked, flash, onSelect, onLetter, onBackspace }) {
  const cellRefs = useRef({});
  const wrapRef = useRef(null);
  // ONE hidden input captures all typing. Because focus never moves between cells,
  // Android can't fire stray focus events that reset the active cell to the word start.
  const inputRef = useRef(null);
  // Handlers read the latest active/direction from refs so a fast keystroke that arrives
  // before React re-renders still targets the correct (current) cell.
  const activeRef = useRef(active);
  activeRef.current = active;
  const dirRef = useRef(direction);
  dirRef.current = direction;

  const white = (r, c) => r >= 0 && r < size && c >= 0 && c < size && layout[r] && layout[r][c];

  const inWord = (r, c) => {
    if (!active) return false;
    const dr = direction === "across" ? 0 : 1;
    const dc = direction === "across" ? 1 : 0;
    if (direction === "across" && r !== active.r) return false;
    if (direction === "down" && c !== active.c) return false;
    let sr = active.r, sc = active.c;
    while (white(sr - dr, sc - dc)) { sr -= dr; sc -= dc; }
    let er = active.r, ec = active.c;
    while (white(er + dr, ec + dc)) { er += dr; ec += dc; }
    if (direction === "across") return r === active.r && c >= sc && c <= ec;
    return c === active.c && r >= sr && r <= er;
  };

  const resetBuffer = () => {
    const inp = inputRef.current;
    if (inp) { try { inp.value = " "; } catch (e) {} }
  };

  // Keep the single input positioned over the active cell (so the mobile keyboard/caret
  // anchors correctly and the browser keeps the active square in view). Re-focus ONLY if
  // focus was lost (e.g. after tapping a clue in the list) — never during letter advance.
  useLayoutEffect(() => {
    const inp = inputRef.current;
    if (!inp || !active) return;
    const cell = cellRefs.current[key(active.r, active.c)];
    if (cell) {
      inp.style.top = cell.offsetTop + "px";
      inp.style.left = cell.offsetLeft + "px";
      inp.style.width = cell.offsetWidth + "px";
      inp.style.height = cell.offsetHeight + "px";
    }
    if (document.activeElement !== inp) {
      resetBuffer();
      try { inp.focus({ preventScroll: true }); } catch (e) {}
    }
  }, [active, size]);

  const focusInput = () => {
    const inp = inputRef.current;
    if (!inp) return;
    resetBuffer();
    try { inp.focus({ preventScroll: true }); } catch (e) {}
  };

  // Focus on the completed tap, after touch release, while still inside the user gesture.
  const selectCell = (r, c) => { onSelect({ r, c }, { tap: true }); focusInput(); };

  const arrow = (r, c, dr, dc, dir) => {
    if (white(r + dr, c + dc)) onSelect({ r: r + dr, c: c + dc }, { dir });
    else onSelect({ r, c }, { dir });
  };

  const handleKeyDown = (e) => {
    const a = activeRef.current;
    if (!a) return;
    const k = e.key;
    if (k === "Backspace") { e.preventDefault(); onBackspace(a.r, a.c); return; }
    if (k === "Delete") { e.preventDefault(); onLetter(a.r, a.c, ""); return; }
    if (k === "ArrowRight") { e.preventDefault(); arrow(a.r, a.c, 0, 1, "across"); return; }
    if (k === "ArrowLeft") { e.preventDefault(); arrow(a.r, a.c, 0, -1, "across"); return; }
    if (k === "ArrowDown") { e.preventDefault(); arrow(a.r, a.c, 1, 0, "down"); return; }
    if (k === "ArrowUp") { e.preventDefault(); arrow(a.r, a.c, -1, 0, "down"); return; }
    if (k === " ") { e.preventDefault(); onSelect({ r: a.r, c: a.c }, { tap: true }); return; }
    // A letter always REPLACES the active cell and advances (handled in parent onLetter).
    if (isLetter(k)) { e.preventDefault(); onLetter(a.r, a.c, k.toUpperCase()); }
  };

  // Mobile / IME fallback: onKeyDown often lacks the character, so derive it here.
  // We keep a single space as a buffer and reset it after every event so that:
  //  - inserting a letter yields exactly one new character, and
  //  - backspace always has the buffer space to delete (firing a delete inputType).
  const handleInput = (e) => {
    const a = activeRef.current;
    const it = (e.nativeEvent && e.nativeEvent.inputType) || "";
    const raw = e.target.value || "";
    resetBuffer();
    if (!a) return;
    if (it.indexOf("delete") === 0) { onBackspace(a.r, a.c); return; }
    const ch = raw.replace(/[^a-zA-Z]/g, "").slice(-1).toUpperCase();
    if (ch) onLetter(a.r, a.c, ch);
  };

  return (
    <div ref={wrapRef} className="relative overflow-auto no-scrollbar border-2 border-white/20 bg-black p-1 sm:p-2" data-testid="crossword-grid">
      <input
        ref={inputRef}
        data-testid="crossword-input"
        defaultValue=" "
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        aria-label="Crossword letter entry"
        inputMode="text"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        className="absolute z-10 opacity-0 caret-transparent border-0 bg-transparent p-0"
        style={{ top: 0, left: 0, width: 1, height: 1, fontSize: 16, pointerEvents: "none" }}
      />
      <div className="grid mx-auto" style={{ gridTemplateColumns: `repeat(${size}, minmax(0,1fr))`, width: "min(100%, 560px)" }}>
        {layout.map((row, r) =>
          row.map((cell, c) => {
            if (!cell) return <div key={key(r, c)} className="aspect-square bg-black" />;
            const k = key(r, c);
            const isActive = active && active.r === r && active.c === c;
            const highlight = inWord(r, c);
            const isRevealed = revealed?.has(k);
            const isWrong = wrong?.has(k);
            const isLockedCell = locked?.has(k);
            const isFlash = flash?.has(k);
            return (
              <div
                key={k}
                ref={(el) => (cellRefs.current[k] = el)}
                className={`relative aspect-square border border-white/15 cursor-pointer${isFlash ? " cw-flash" : ""}`}
                data-testid={`cell-${r}-${c}`}
                data-locked={isLockedCell ? "1" : undefined}
                onPointerDown={(e) => { e.preventDefault(); }}
                onClick={() => selectCell(r, c)}
                style={{ background: isActive ? "#FACC15" : isLockedCell ? "#BBF7D0" : highlight ? "#FDE68A" : isWrong ? "rgba(239,68,68,0.35)" : "#f4f4f0" }}>
                {cell.number && (
                  <span className="absolute top-0 left-0.5 text-[7px] sm:text-[8px] font-bold leading-none pointer-events-none" style={{ color: isActive ? "#0A0A0A" : "#333" }}>{cell.number}</span>
                )}
                {/* Read-only display of the entered letter (kept as an input for stable test ids). */}
                <input
                  data-testid={`input-${r}-${c}`}
                  value={values[r][c] || ""}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className="w-full h-full text-center bg-transparent outline-none font-bold uppercase text-[11px] sm:text-base caret-transparent"
                  style={{ color: isRevealed ? "#0891b2" : "#0A0A0A", pointerEvents: "none" }}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
